import React, {useState, useEffect, useRef, useMemo, use} from "react";
import API from "../services/ApiService"
import GhostSearch from "./GhostSearch";
import { useAuth } from "../context/AuthContext";
import JSConfetti from "js-confetti";
import Ranking from "./Ranking";
import { safeApiCall } from "../utils/safeApiCall";
import { toast } from "react-toastify";
import ErrorBoundary from "./ErrorBoundary/ErrorBoundary";
import { CiCircleInfo } from "react-icons/ci";

export default function GuessGhost(props){
    const [ghosts, setGhosts] = useState([])
    const [answers, setAnswers] = useState([])
    const [excludedIds, setExcludedIds] = useState([])
    const { isLoggedIn, contextUser } = useAuth();
    const newRowRef = useRef(null);
    const [justLoading, setJustLoading] = useState(true);
    const confettiRef = useRef(null);
    const finished = useMemo(() => answers.some(ghost => ghost.is_correct), [answers]);
    const [query, setQuery] = useState("")

    if (!confettiRef.current) {
        confettiRef.current = new JSConfetti();
    }

    useMemo(() => {
        if (finished && newRowRef.current != null) {
            confettiRef.current.addConfetti({
                confettiNumber: 10,
                emojis: ['👻', '🎉', '💀'],
            });
        }
    }, [finished]);

    useEffect (()=>{
        const getGhosts = async () => {
            const response = await safeApiCall(API.listGhosts, {data: []}, (err) => toast.error("Error getting ghosts"));
            setGhosts(response.data);
        }
        getGhosts();
    }, [])

    useEffect (() => {
        const getAnswers = async () => {
            const response = await safeApiCall(API.getGhostAnswers, {data: []}, (err) => toast.error("Error getting your answers"));
            setAnswers(response.data);
            setExcludedIds(prev => [...response.data.map( answer => answer.id ), ...prev]);
        }
        if (contextUser) {
            getAnswers();
        } else {
            setAnswers([]);
        }
    }, [contextUser])

    useEffect(() => {
        if (!newRowRef.current) return;
        const el = newRowRef.current;
        setTimeout(() => {
            el.classList.add("show");
        }, 50); 
    }, [answers]);

    async function handleGhostSelect(id){
        try {
            const response = await safeApiCall(() => API.ghostAnswer(id), undefined, (err) => toast.error("Error submitting answer"));
            if (!response) return;
            setJustLoading(false);
            setExcludedIds(prev => [...prev, id])
            setAnswers(prev => [...prev, response.data])
            setQuery("")
        } catch (err) {
            toast.error("Unexpected error submitting answer");
        }
    }

    function renderAnswers() {
        let ghostNumber = 0;
        return (
        <ul className="answers-list" id="answers-list">
                <li id="ghost-answers-first-row"><p>Ghost image</p><p>Ghost name</p><p className="hunt-threshold-title">Hunt Threshold<CiCircleInfo className="hunt-threshold-title-info" title="The highest possible hunt threshold (including special abilities)"/></p><p>First Evidence</p><p>Second Evidence</p><p>Third Evidence</p></li>
                {answers.slice().reverse().map(ghost => {
                    if (ghosts.length === 0) return null;
                    const refGhost = ghosts.data.find( g => g.id === ghost.id);
                    if (!refGhost) return null;
                    if (ghostNumber === 0){
                        ghostNumber++;
                        return (
                            <li key={ghost.id} className={`answer-row ${justLoading ? 'show' : ''}`}
                            ref={(el) => {
                                if (justLoading) {
                                    return null;
                                } else {
                                    return newRowRef.current = el;
                                }
                            }}>
                                <img
                                    src={refGhost.image}
                                    alt={`${refGhost.name}`}
                                    className="ghost-answers-image"
                                />
                                <span className={`answer-${ghost.is_correct}`}>{refGhost.name}</span>
                                <div className={`answer-${ghost.hunt_threshold} hunt-threshold`}>
                                    <span>{ghost.hunt_threshold == "higher" ? "▲" : "\u00A0"}</span>
                                    <span>{refGhost.hunt_threshold}%</span>
                                    <span>{ghost.hunt_threshold == "lower" ? "▼" : "\u00A0"}</span>
                                    </div>
                                <span className={`answer-${ghost.first_evidence}`}>{refGhost.evidences[0]}</span>
                                <span className={`answer-${ghost.second_evidence}`}>{refGhost.evidences[1]}</span>
                                <span className={`answer-${ghost.third_evidence}`}>{refGhost.evidences[2]}</span>
                            </li>
                        )
                    }
                    return (
                    <li key={ghost.id}>
                        <img
                            src={refGhost.image}
                            alt={`${refGhost.name}`}
                            className="ghost-answers-image"
                        />
                        <span className={`answer-${ghost.is_correct}`}>{refGhost.name}</span>
                        <div className={`answer-${ghost.hunt_threshold} hunt-threshold`}>
                            <span>{ghost.hunt_threshold == "higher" ? "▲" : "\u00A0"}</span>
                            <span>{refGhost.hunt_threshold}%</span>
                            <span>{ghost.hunt_threshold == "lower" ? "▼" : "\u00A0"}</span>
                            </div>
                        <span className={`answer-${ghost.first_evidence}`}>{refGhost.evidences[0]}</span>
                        <span className={`answer-${ghost.second_evidence}`}>{refGhost.evidences[1]}</span>
                        <span className={`answer-${ghost.third_evidence}`}>{refGhost.evidences[2]}</span>
                    </li>
                    );
                })}
            </ul>
        )
    }
    return(
        <div className="guess-ghost">
            <div className="left-sidebar">
            </div>
            <div className="main-content">
                <ErrorBoundary>
                    <GhostSearch ghosts={ghosts.data} onAnswerSelect={handleGhostSelect} excludedIds={excludedIds} finished={finished} query={query} setQuery={setQuery} />
                </ErrorBoundary>
                <ErrorBoundary>
                    {answers.length > 0 && renderAnswers()}
                </ErrorBoundary>
            </div>
            <div className="right-sidebar">
                <ErrorBoundary>
                    <Ranking rankingService={API.getGhostScores} streakService={API.getGhostStreak} color="neon-red" finished={finished} userUpdate={newRowRef}/>
                </ErrorBoundary>
            </div>
        </div>
    )
}